// v2/hooks/useQuotes.js — Teklif modulu: API cagrilari, listeler, dosya indirme
// Backend: myapp/v2_teklif.py, v2_teklif_cikti.py, v2_teklif_islem.py
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, qs, API_BASE, ApiError, OTURUM_BITTI } from '../utils/api';
import { oturumBilgisi, oturumuSonlandir } from '../utils/authStore';
import { formatNumber } from '../utils/format';

// ---------------------------------------------------------------- sabitler
export const QUOTE_STATUS = {
  'Hazır':            { label: 'Hazır',             tone: 'tBlue' },
  'Sepete Aktarıldı': { label: 'Sepete Aktarıldı',  tone: 'tAmber' },
  'Siparişe Dönüştü': { label: 'Siparişe Dönüştü',  tone: 'tGreen' },
  'Süresi Doldu':     { label: 'Süresi Doldu',      tone: 'tGray' },
  'İptal':            { label: 'İptal',             tone: 'tGray' },
};

export const LINE_STATUS = {
  tam:        { label: 'Tamamı Karşılanır', short: 'Tamamı',        tone: 'tGreen' },
  kismi:      { label: 'Kısmen Karşılanır', short: 'Kısmen',        tone: 'tAmber' },
  yok:        { label: 'Stok Yok',          short: 'Stok Yok',      tone: 'tRed' },
  fiyatsiz:   { label: 'Fiyat Sorunuz',     short: 'Fiyat Sorunuz', tone: 'tBlue' },
  kontrol:    { label: 'Kontrol Gerekli',   short: 'Kontrol',       tone: 'tYellow' },
  bulunamadi: { label: 'Bulunamadı',        short: 'Bulunamadı',    tone: 'tGray' },
};
export const LINE_STATUS_ORDER = ['tam', 'kismi', 'yok', 'fiyatsiz', 'kontrol', 'bulunamadi'];

export const MATCH_LABEL = {
  kod: '',
  sifir: 'Baştaki sıfırlar tamamlandı',
  eski: 'Eski Bosch kodu',
  oem: 'OEM numarası',
  coklu: 'OEM — birden fazla ürün',
  secim: 'Seçiminiz',
  ikame: 'İkame ürün',
  yok: '',
};

export const WARNING_LABEL = {
  tekrar: 'Tekrarlanan kod (miktarlar birleştirildi)',
  miktar_bos: 'Miktar boş (1 adet kabul edildi)',
  miktar_gecersiz: 'Geçersiz miktar (satır atlandı)',
  miktar_buyuk: 'Çok büyük miktar (satır atlandı)',
  yuvarlandi: 'Ondalıklı miktar (yukarı yuvarlandı)',
  kod_bos: 'Ürün kodu boş (satır atlandı)',
  kod_gecersiz: 'Geçersiz ürün kodu (satır atlandı)',
};

// ---------------------------------------------------------------- yardimci
export function fmtTarih(iso, saat = true) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  const t = `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
  return saat ? `${t} ${p(d.getHours())}:${p(d.getMinutes())}` : t;
}

/** Gecerlilige kalan gun (bugun biterse 0, gectiyse negatif) */
export function kalanGun(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

/** Euro tutari: 1.234,56 € (bos/null -> '') */
export function fmtEur(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '';
  return `${formatNumber(Number(v), 2)} €`;
}

export function karsilamaOrani(q) {
  if (!q || !q.requestedQty) return 0;
  return Math.min(100, (q.suppliedQty * 100) / q.requestedQty);
}

/** Yetkili dosya indirme (PDF / Excel / sablon) — api.js metin dondurdugu icin ayri */
export async function dosyaIndir(yol, varsayilanAd) {
  const { token } = oturumBilgisi();
  let res;
  try {
    res = await fetch(`${API_BASE}${yol}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (e) {
    throw new ApiError('Sunucuya ulaşılamadı', 0, null);
  }
  if (res.status === 401 || res.status === 403) {
    oturumuSonlandir();
    throw new ApiError(OTURUM_BITTI, res.status, null);
  }
  if (!res.ok) {
    let mesaj = 'Dosya indirilemedi';
    try {
      const j = await res.json();
      mesaj = j.message || mesaj;
    } catch (e) { /* json degil */ }
    throw new ApiError(mesaj, res.status, null);
  }
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const ad = m ? decodeURIComponent(m[1]) : varsayilanAd;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ad;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ---------------------------------------------------------------- islemler
export const quoteApi = {
  create(file, note) {
    const fd = new FormData();
    fd.append('file', file);
    if (note) fd.append('note', note);
    return apiPost('/v2/quotes/create/', fd);
  },
  toCart(no, lineIds, dryRun) {
    return apiPost(`/v2/quotes/${encodeURIComponent(no)}/to-cart/`, {
      lineIds: lineIds && lineIds.length ? lineIds : null,
      dryRun: Boolean(dryRun),
    });
  },
  createOrder(itemIds, aciklama) {
    return apiPost('/v2/orders/create/', { itemIds, aciklama });
  },
  ordered(no, itemIds) {
    return apiPost(`/v2/quotes/${encodeURIComponent(no)}/ordered/`, { itemIds });
  },
  resolve(no, lineId, code) {
    return apiPost(`/v2/quotes/${encodeURIComponent(no)}/lines/${lineId}/resolve/`, { code });
  },
  renew(no) {
    return apiPost(`/v2/quotes/${encodeURIComponent(no)}/renew/`, {});
  },
  template() {
    return dosyaIndir('/v2/quotes/template/', 'AS_Otomotiv_Teklif_Sablonu.xlsx');
  },
  download(no, tur, scope) {
    const uz = tur === 'excel' ? 'xlsx' : 'pdf';
    const ek = scope === 'supplied' ? '?scope=supplied' : '';
    return dosyaIndir(`/v2/quotes/${encodeURIComponent(no)}/${tur}/${ek}`, `${no}.${uz}`);
  },
};

// ---------------------------------------------------------------- hook'lar
/** Tekliflerim listesi */
export function useQuoteList(page, per) {
  const [state, setState] = useState({
    items: [], totalItems: 0, totalPages: 1, loading: true, error: null,
  });
  const [tik, setTik] = useState(0);
  const yenile = useCallback(() => setTik((t) => t + 1), []);

  useEffect(() => {
    let iptal = false;
    setState((s) => ({ ...s, loading: s.items.length === 0, error: null }));
    apiGet(`/v2/quotes/${qs({ page, per_page: per })}`)
      .then((v) => {
        if (iptal) return;
        setState({
          items: v.results || [], totalItems: v.total_items || 0,
          totalPages: v.total_pages || 1, loading: false, error: null,
        });
      })
      .catch((e) => {
        if (iptal || e.message === OTURUM_BITTI) return;
        setState((s) => ({ ...s, loading: false, error: e.message || 'Teklifler alınamadı' }));
      });
    return () => { iptal = true; };
  }, [page, per, tik]);

  return { ...state, yenile };
}

/** Tek teklif ozeti */
export function useQuote(no) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tik, setTik] = useState(0);
  const yenile = useCallback(() => setTik((t) => t + 1), []);

  useEffect(() => {
    let iptal = false;
    apiGet(`/v2/quotes/${encodeURIComponent(no)}/`)
      .then((v) => { if (!iptal) { setQuote(v.quote); setError(null); setLoading(false); } })
      .catch((e) => {
        if (iptal || e.message === OTURUM_BITTI) return;
        setError(e.status === 404 ? 'Teklif bulunamadı' : (e.message || 'Teklif alınamadı'));
        setLoading(false);
      });
    return () => { iptal = true; };
  }, [no, tik]);

  return { quote, setQuote, loading, error, yenile };
}

/** Teklif kalemleri (sunucu tarafli filtre + sayfa) */
export function useQuoteLines(no, { status, q, page, per }) {
  const [state, setState] = useState({
    lines: [], totalItems: 0, totalPages: 1, loading: true, refreshing: false, error: null,
  });
  const [tik, setTik] = useState(0);
  const yenile = useCallback(() => setTik((t) => t + 1), []);
  const ilk = useRef(true);

  useEffect(() => {
    let iptal = false;
    setState((s) => ({ ...s, loading: ilk.current, refreshing: !ilk.current, error: null }));
    apiGet(`/v2/quotes/${encodeURIComponent(no)}/lines/${qs({
      status: status === 'all' ? '' : status, q, page, per_page: per,
    })}`)
      .then((v) => {
        if (iptal) return;
        ilk.current = false;
        setState({
          lines: v.results || [], totalItems: v.total_items || 0, totalPages: v.total_pages || 1,
          loading: false, refreshing: false, error: null,
        });
      })
      .catch((e) => {
        if (iptal || e.message === OTURUM_BITTI) return;
        setState((s) => ({ ...s, loading: false, refreshing: false,
                           error: e.message || 'Kalemler alınamadı' }));
      });
    return () => { iptal = true; };
  }, [no, status, q, page, per, tik]);

  /** Tek kalemi yerinde guncelle (cozumleme sonrasi) */
  const kalemGuncelle = useCallback((yeni) => {
    setState((s) => ({ ...s, lines: s.lines.map((l) => (l.id === yeni.id ? yeni : l)) }));
  }, []);

  return { ...state, yenile, kalemGuncelle };
}
